<script setup lang="ts">
import type { PortalModalProps } from '.';

import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger
} from '@/components/ui/dialog';
import { cn } from '@/utils';
import { useEventListener } from '@vueuse/core';

type Emit = {
	close: [];
};

const props = withDefaults(defineProps<PortalModalProps>(), {
	title: '',
	showCloseIcon: true,
	description: '',
	preventInteractOutside: false
});

const emit = defineEmits<Emit>();

function handleInteractOutside(event: Event) {
	if (props.preventInteractOutside) {
		event.preventDefault();
	}
}

useEventListener(window, 'keydown', event => {
	if (event.key === 'Escape') {
		emit('close');
	}
});
</script>

<template>
	<Dialog>
		<DialogTrigger as-child class="w-full">
			<slot name="trigger"></slot>
		</DialogTrigger>

		<DialogContent
			:class="cn('max-h-[95%]', contentClass)"
			:overlay-class="overlayClass"
			:show-close-icon="showCloseIcon"
			v-bind="description ? { description } : {}"
			@interact-outside="handleInteractOutside"
		>
			<DialogHeader :class="cn(showCloseIcon ? 'pr-8' : '', headerClass)">
				<slot name="header">
					<DialogTitle
						:class="
							cn(
								'text-wrap font-matter-sq text-2xl font-normal leading-[150%] tracking-tighter text-primary',
								titleClass
							)
						"
					>
						<slot name="title">
							{{ title }}
						</slot>
					</DialogTitle>
					<DialogDescription />
				</slot>
			</DialogHeader>

			<slot></slot>

			<DialogFooter
				:class="cn('flex items-center justify-between', footerClass)"
			>
				<slot name="footer"></slot>
			</DialogFooter>
		</DialogContent>
	</Dialog>
</template>
